export type SegmentationData = {
    sdice1: number | null;
    sdice3: number | null;
    sdice5: number | null;
    average_apl_mm: number | null;
    total_apl_mm: number | null;
    hd: number | null;
    mean_hd: number | null;
    user_z_length_mm: number | null;
    gt_z_length_mm: number | null;
    dice: number | null;
    sdice2: number | null;
    hd95: number | null;
    user_vol_mm3: number | null;
    gt_vol_mm3: number | null;
    gt_roi_name: string | null;
    user_roi_name: string | null;
};

// The worker writes NaN and infinity as this value (its JSON encoder cannot carry them).
const NOT_A_NUMBER_CODE = 999;
// The worker's HD95 for a structure missing from either set.
const MISSING_HD95_CODE = 99999;

const FRACTIONS = ['dice', 'sdice1', 'sdice2', 'sdice3', 'sdice5'] as const;
// Per-structure distances, which never reach a meter inside a patient.
const DISTANCES_MM = ['hd', 'mean_hd', 'average_apl_mm'] as const;
// Sums and extents: 999 can be a real reading (333 slices of 3 mm), so never decoded.
const MEASURED_AS_IS = [
    'total_apl_mm',
    'gt_z_length_mm',
    'user_z_length_mm',
    'gt_vol_mm3',
    'user_vol_mm3',
] as const;

export class SegmentationMetricsFormatError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'SegmentationMetricsFormatError';
    }
}

function numberOrNull(value: unknown): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function nameOrNull(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
}

// The NaN code is read only where it cannot be a measurement; a 999 total or volume is real.
export function decodeSegmentationMetrics(raw: unknown): SegmentationData[] {
    if (!Array.isArray(raw)) {
        throw new SegmentationMetricsFormatError(
            'Segmentation metrics result is not a list of structures.'
        );
    }
    return raw.map((row): SegmentationData => {
        if (typeof row !== 'object' || row === null) {
            throw new SegmentationMetricsFormatError(
                'Segmentation metrics result holds a malformed structure row.'
            );
        }
        const fields = row as Record<string, unknown>;
        const decoded: Record<string, number | string | null> = {
            gt_roi_name: nameOrNull(fields.gt_roi_name),
            user_roi_name: nameOrNull(fields.user_roi_name),
        };
        for (const key of [...FRACTIONS, ...DISTANCES_MM]) {
            const value = numberOrNull(fields[key]);
            decoded[key] = value === NOT_A_NUMBER_CODE ? null : value;
        }
        const hd95 = numberOrNull(fields.hd95);
        decoded.hd95 =
            hd95 === MISSING_HD95_CODE || hd95 === NOT_A_NUMBER_CODE
                ? null
                : hd95;
        for (const key of MEASURED_AS_IS) {
            decoded[key] = numberOrNull(fields[key]);
        }
        return decoded as SegmentationData;
    });
}
